import { Controller } from "stimulus";
import { Calendar } from "@fullcalendar/core";
import dayGridPlugin from "@fullcalendar/daygrid";
import timeGridPlugin from "@fullcalendar/timegrid";
import interactionPlugin from "@fullcalendar/interaction";
import listPlugin from "@fullcalendar/list";

export default class extends Controller {
  static targets = [
    "calendar",
    "locationFilter",
    "roomFilter",
    "modal",
    "modalTitle",
    "modalSubtitle",
    "modalBody",
    "modalFooter",
  ];

  static values = {
    eventsUrl: String,
    canManage: Boolean,
    newBookingUrl: String,
    dayStartHour: { type: Number, default: 8 },
    dayEndHour: { type: Number, default: 17 },
  };

  connect() {
    this.initializeCalendar();
  }

  disconnect() {
    if (this.calendar) {
      this.calendar.destroy();
      this.calendar = null;
    }
  }

  initializeCalendar() {
    this.calendar = new Calendar(this.calendarTarget, {
      plugins: [dayGridPlugin, timeGridPlugin, interactionPlugin, listPlugin],
      initialView: "dayGridMonth",
      headerToolbar: {
        left: "prev,next today",
        center: "title",
        right: "dayGridMonth,timeGridWeek,timeGridDay,listWeek",
      },
      buttonText: {
        today: "Today",
        month: "Month",
        week: "Week",
        day: "Day",
        list: "List",
      },
      height: "auto",
      expandRows: true,
      navLinks: true,
      nowIndicator: true,
      firstDay: 1,
      slotMinTime: "06:00:00",
      slotMaxTime: "22:00:00",
      eventTimeFormat: { hour: "numeric", minute: "2-digit", meridiem: "short" },
      noEventsText: "No bookings in this range",
      // Timed events default to a dot in month view, which hides both the
      // category colour and how much of the day a booking takes up. This is
      // resolved when events are parsed, so it cannot be set per view.
      eventDisplay: "block",
      eventOrderStrict: true,
      eventOrder: (a, b) => {
        const startA = a.start ? a.start.valueOf() : 0;
        const startB = b.start ? b.start.valueOf() : 0;
        if (startA !== startB) return startA - startB;

        const locationA = a.extendedProps?.locationPosition ?? 9999;
        const locationB = b.extendedProps?.locationPosition ?? 9999;
        if (locationA !== locationB) return locationA - locationB;

        const roomA = a.extendedProps?.roomPosition ?? 9999;
        const roomB = b.extendedProps?.roomPosition ?? 9999;
        if (roomA !== roomB) return roomA - roomB;

        return (a.title || "").localeCompare(b.title || "");
      },
      views: {
        dayGridMonth: {
          dayMaxEvents: 3,
          eventContent: (arg) => this.renderMonthEvent(arg),
        },
        timeGrid: {
          displayEventEnd: false,
          displayEventTime: false,
          slotEventOverlap: false,
          eventMaxStack: 3,
          eventMinHeight: 36,
          eventShortHeight: 28,
          eventContent: (arg) => this.renderTimeGridEvent(arg),
        },
      },
      events: this.buildEventsSource(),
      eventClick: (info) => {
        info.jsEvent.preventDefault();
        this.openBookingModal(info.event);
      },
      dateClick: (info) => {
        if (!this.canManageValue) return;

        const params = new URLSearchParams({ date: info.dateStr.slice(0, 10) });
        if (this.hasRoomFilterTarget && this.roomFilterTarget.value) {
          params.set("room_id", this.roomFilterTarget.value);
        }
        window.location.href = `${this.newBookingUrlValue}?${params.toString()}`;
      },
    });

    this.calendar.render();
  }

  buildEventsSource() {
    return (fetchInfo, successCallback, failureCallback) => {
      const params = new URLSearchParams({
        start: fetchInfo.startStr,
        end: fetchInfo.endStr,
      });

      if (this.hasRoomFilterTarget && this.roomFilterTarget.value) {
        params.set("room_id", this.roomFilterTarget.value);
      }

      if (this.hasLocationFilterTarget && this.locationFilterTarget.value) {
        params.set("location_id", this.locationFilterTarget.value);
      }

      fetch(`${this.eventsUrlValue}?${params.toString()}`, {
        headers: { Accept: "application/json" },
        credentials: "same-origin",
      })
        .then((response) => {
          if (!response.ok) throw new Error("Unable to load bookings");
          return response.json();
        })
        .then((events) => successCallback(events))
        .catch((error) => failureCallback(error));
    };
  }

  roomFilterChanged() {
    if (this.calendar) this.calendar.refetchEvents();
  }

  locationFilterChanged() {
    this.syncRoomOptions();
    if (this.calendar) this.calendar.refetchEvents();
  }

  syncRoomOptions() {
    if (!this.hasRoomFilterTarget) return;

    const locationId = this.hasLocationFilterTarget
      ? this.locationFilterTarget.value
      : "";
    const selectedRoomId = this.roomFilterTarget.value;

    Array.from(this.roomFilterTarget.options).forEach((option) => {
      if (!option.value) return;

      const matches =
        !locationId || option.dataset.locationId === locationId;
      option.hidden = !matches;
      option.disabled = !matches;
    });

    const selectedOption = this.roomFilterTarget.selectedOptions[0];
    if (selectedOption && (selectedOption.hidden || selectedOption.disabled)) {
      this.roomFilterTarget.value = "";
    } else if (
      selectedRoomId &&
      !Array.from(this.roomFilterTarget.options).some(
        (option) => option.value === selectedRoomId && !option.hidden
      )
    ) {
      this.roomFilterTarget.value = "";
    }
  }

  // Month cells are only one column wide, so duration is shown two ways: the
  // time range in text, and a bar whose offset and width map onto the bookable
  // day (a morning booking fills the left half, an afternoon the right).
  renderMonthEvent(arg) {
    const props = arg.event.extendedProps || {};

    const wrapper = document.createElement("div");
    wrapper.className = "rb-event";

    const head = document.createElement("div");
    head.className = "rb-event__head";

    if (props.locationShortCode) {
      const badge = document.createElement("span");
      badge.className = "rb-event__loc";
      badge.textContent = props.locationShortCode;
      badge.title = props.locationName || props.locationShortCode;
      badge.setAttribute("aria-label", props.locationName || props.locationShortCode);
      this.styleLocationBadge(badge, arg);
      head.appendChild(badge);
    }

    const title = document.createElement("span");
    title.className = "rb-event__title";
    title.textContent = props.eventName || arg.event.title;
    head.appendChild(title);
    wrapper.appendChild(head);

    const meta = document.createElement("span");
    meta.className = "rb-event__meta";
    const metaBase = document.createElement("span");
    metaBase.className = "rb-event__meta-base";
    metaBase.textContent = [this.compactRange(arg.event.start, arg.event.end), props.roomName]
      .filter(Boolean)
      .join(" · ");
    meta.appendChild(metaBase);
    if (props.openShiftNeedsStaff) {
      meta.appendChild(this.buildOpenShiftMetaLabel(arg));
    }
    wrapper.appendChild(meta);

    wrapper.appendChild(this.buildDurationBar(arg));

    return { domNodes: [wrapper] };
  }

  // Week/day columns stack one booking per room. Lead with the room name so
  // narrow side-by-side blocks stay scannable; eventMaxStack handles overflow.
  renderTimeGridEvent(arg) {
    const props = arg.event.extendedProps || {};

    const wrapper = document.createElement("div");
    wrapper.className = "rb-event rb-event--timed";

    const head = document.createElement("div");
    head.className = "rb-event__head";

    if (props.locationShortCode) {
      const badge = document.createElement("span");
      badge.className = "rb-event__loc rb-event__loc--timed";
      badge.textContent = props.locationShortCode;
      badge.title = props.locationName || props.locationShortCode;
      badge.setAttribute("aria-label", props.locationName || props.locationShortCode);
      this.styleLocationBadge(badge, arg);
      head.appendChild(badge);
    }

    const room = document.createElement("span");
    room.className = "rb-event__room";
    room.textContent = props.roomName || "Room";
    head.appendChild(room);
    this.appendOpenShiftIndicator(head, props, arg);
    wrapper.appendChild(head);

    const title = document.createElement("span");
    title.className = "rb-event__title rb-event__title--timed";
    title.textContent = props.eventName || arg.event.title;
    wrapper.appendChild(title);

    return { domNodes: [wrapper] };
  }

  styleLocationBadge(badge, arg) {
    const lightText = (arg.event.textColor || arg.textColor || "") === "#ffffff";
    badge.style.backgroundColor = lightText
      ? "rgba(255, 255, 255, 0.28)"
      : "rgba(15, 23, 42, 0.14)";
    badge.style.color = lightText ? "#ffffff" : "inherit";
  }

  appendOpenShiftIndicator(container, props, arg) {
    if (!props.openShiftNeedsStaff) return;

    const badge = document.createElement("span");
    badge.className = "rb-event__os";
    badge.title = "Need staff";
    badge.setAttribute("aria-label", "Need staff");
    badge.innerHTML =
      '<i class="fas fa-user-clock" aria-hidden="true"></i><span class="rb-event__os-text">Need Staff</span>';
    this.styleOpenShiftBadge(badge, arg);
    container.appendChild(badge);
  }

  buildOpenShiftMetaLabel(arg) {
    const label = document.createElement("span");
    label.className = "rb-event__os-label";
    label.textContent = "Need Staff";
    this.styleOpenShiftMetaLabel(label, arg);
    return label;
  }

  styleOpenShiftBadge(badge, arg) {
    const lightText = (arg.event.textColor || arg.textColor || "") === "#ffffff";

    if (lightText) {
      badge.style.backgroundColor = "#ffffff";
      badge.style.color = "#0f766e";
      badge.style.boxShadow = "0 0 0 1px rgba(15, 23, 42, 0.12)";
    } else {
      badge.style.backgroundColor = "#0f766e";
      badge.style.color = "#ffffff";
      badge.style.boxShadow = "0 0 0 1px rgba(15, 23, 42, 0.18)";
    }
  }

  styleOpenShiftMetaLabel(label, arg) {
    const lightText = (arg.event.textColor || arg.textColor || "") === "#ffffff";

    if (lightText) {
      label.style.backgroundColor = "rgba(255, 255, 255, 0.92)";
      label.style.color = "#0f766e";
      label.style.boxShadow = "0 0 0 1px rgba(15, 23, 42, 0.1)";
    } else {
      label.style.backgroundColor = "#0f766e";
      label.style.color = "#ffffff";
      label.style.boxShadow = "0 0 0 1px rgba(15, 23, 42, 0.18)";
    }
  }

  buildDurationBar(arg) {
    const { offset, width } = this.dayOccupancy(arg.event.start, arg.event.end);
    const lightText = (arg.event.textColor || arg.textColor || "") === "#ffffff";

    const track = document.createElement("span");
    track.className = "rb-event__bar";
    track.style.backgroundColor = lightText
      ? "rgba(255, 255, 255, 0.32)"
      : "rgba(15, 23, 42, 0.16)";

    const fill = document.createElement("span");
    fill.className = "rb-event__bar-fill";
    fill.style.left = `${offset * 100}%`;
    fill.style.width = `${width * 100}%`;
    track.appendChild(fill);

    return track;
  }

  dayOccupancy(start, end) {
    if (!start || !end) return { offset: 0, width: 1 };

    // Bookings that cross midnight cover the whole cell.
    if (start.toDateString() !== end.toDateString()) {
      return { offset: 0, width: 1 };
    }

    const span = this.dayEndHourValue - this.dayStartHourValue;
    if (span <= 0) return { offset: 0, width: 1 };

    const toFraction = (date) => {
      const hours = date.getHours() + date.getMinutes() / 60;
      return this.clamp((hours - this.dayStartHourValue) / span, 0, 1);
    };

    const offset = toFraction(start);
    const width = Math.max(toFraction(end) - offset, 0.08);

    return { offset: Math.min(offset, 1 - width), width };
  }

  clamp(value, min, max) {
    return Math.min(Math.max(value, min), max);
  }

  compactRange(start, end) {
    if (!start) return "";
    if (!end) return this.compactTime(start);

    return `${this.compactTime(start)}–${this.compactTime(end)}`;
  }

  compactTime(date) {
    const hours = date.getHours();
    const minutes = date.getMinutes();
    const suffix = hours < 12 ? "am" : "pm";
    const hour12 = hours % 12 === 0 ? 12 : hours % 12;

    return minutes === 0
      ? `${hour12}${suffix}`
      : `${hour12}:${String(minutes).padStart(2, "0")}${suffix}`;
  }

  openBookingModal(event) {
    const props = event.extendedProps || {};

    this.modalTitleTarget.textContent = props.eventName || event.title;
    this.modalSubtitleTarget.textContent = this.formatRange(
      event.start,
      event.end
    );

    const rows = [
      ["Room", props.roomName],
      ["Location", props.locationName],
      ["Time slot", props.timeSlotLabel],
      ["Category", props.categoryName || "Not linked to an event"],
      ["Most responsible person", props.mrpName],
      [
        "Staff",
        props.staffNames && props.staffNames.length
          ? props.staffNames.join(", ")
          : "None assigned",
      ],
    ];

    this.modalBodyTarget.innerHTML = "";
    rows.forEach(([label, value]) => {
      if (!value) return;

      const row = document.createElement("div");
      row.className = "rb-detail";

      const labelEl = document.createElement("span");
      labelEl.className = "rb-detail__label";
      labelEl.textContent = label;

      const valueEl = document.createElement("span");
      valueEl.className = "rb-detail__value";
      valueEl.textContent = value;

      row.appendChild(labelEl);
      row.appendChild(valueEl);
      this.modalBodyTarget.appendChild(row);
    });

    if (props.openShiftUrl) {
      const row = document.createElement("div");
      row.className = "rb-detail";

      const labelEl = document.createElement("span");
      labelEl.className = "rb-detail__label";
      labelEl.textContent = "Need staff";

      const valueEl = document.createElement("span");
      valueEl.className = "rb-detail__value";
      valueEl.appendChild(
        this.buildLink(props.openShiftUrl, "Need Staff", "rb-os-booking-indicator rb-os-booking-indicator--inline")
      );

      row.appendChild(labelEl);
      row.appendChild(valueEl);
      this.modalBodyTarget.appendChild(row);
    }

    this.modalFooterTarget.innerHTML = "";
    this.modalFooterTarget.appendChild(
      this.buildLink(props.url, "View details", "btn btn-primary btn-sm")
    );

    if (this.canManageValue && props.editUrl) {
      this.modalFooterTarget.appendChild(
        this.buildLink(props.editUrl, "Edit", "btn btn-outline-secondary btn-sm")
      );
    }

    this.showModal();
  }

  closeModal(event) {
    event.preventDefault();
    this.hideModal();
  }

  buildLink(href, text, className) {
    const link = document.createElement("a");
    link.href = href;
    link.className = className;
    link.textContent = text;
    return link;
  }

  showModal() {
    const $ = window.jQuery || window.$;

    if ($ && $.fn.modal) {
      $(this.modalTarget).modal("show");
      return;
    }

    if (window.bootstrap?.Modal) {
      window.bootstrap.Modal.getOrCreateInstance(this.modalTarget).show();
    }
  }

  hideModal() {
    const $ = window.jQuery || window.$;

    if ($ && $.fn.modal) {
      $(this.modalTarget).modal("hide");
      return;
    }

    if (window.bootstrap?.Modal) {
      const instance = window.bootstrap.Modal.getInstance(this.modalTarget);
      if (instance) {
        instance.hide();
        return;
      }
    }

    this.modalTarget.classList.remove("show");
    this.modalTarget.style.display = "none";
    this.modalTarget.setAttribute("aria-hidden", "true");
    document.body.classList.remove("modal-open");
    document.querySelectorAll(".modal-backdrop").forEach((element) => element.remove());
  }

  formatRange(start, end) {
    if (!start) return "";

    const dateLabel = start.toLocaleDateString(undefined, {
      weekday: "long",
      month: "long",
      day: "numeric",
      year: "numeric",
    });

    const timeOptions = { hour: "numeric", minute: "2-digit" };
    const startTime = start.toLocaleTimeString(undefined, timeOptions);
    if (!end) return `${dateLabel} · ${startTime}`;

    const endTime = end.toLocaleTimeString(undefined, timeOptions);
    return `${dateLabel} · ${startTime} – ${endTime}`;
  }
}
